import React, {
  CSSProperties,
  ImgHTMLAttributes,
  useState,
  useRef,
  useEffect,
} from 'react';
import IconExpand from './IconExpand';
import IconContract from './IconContract';
import styles from './ExpandableImage.module.css';

interface Dimensions {
  contracted: CSSProperties;
  expanded: CSSProperties;
}

const defaultDimensions: Dimensions = {
  expanded: {
    width: '100%',
    maxWidth: '19.5rem',
  },
  contracted: {
    width: '100%',
    maxWidth: '5rem',
  },
};

export interface ExpandableImageProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  expandAriaLabel: string;
  contractAriaLabel: string;
  dimensions?: Dimensions;
  featureName?: string;
  onContract?: () => void;
  onExpand?: () => void;
  onValidImageDetermined?: (isValid: boolean) => void;
}

const ExpandableImage = ({
  expandAriaLabel,
  contractAriaLabel,
  dimensions = defaultDimensions,
  onContract,
  onExpand,
  onValidImageDetermined,
  ...imgAttrs
}: ExpandableImageProps) => {
  const [expanded, setExpanded] = useState(false);
  const img = useRef<HTMLImageElement | null>(null);

  const handleContract = () => {
    setExpanded(false);
  };

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleTransitionEnd = () => {
    const fn = expanded ? onExpand : onContract;
    if (fn) fn();
  };

  useEffect(() => {
    if (!onValidImageDetermined) return;

    // Periodically check to see if the naturalWidth of the image is greater than 0.
    // If it is, we have an image loaded.
    const imageCheck = setInterval(() => {
      const imgEl = img?.current;
      if (imgEl && imgEl.naturalWidth) {
        onValidImageDetermined(true);
        clearInterval(imageCheck);
        clearTimeout(imageCheckTimeout);
      }
    }, 100);

    // Stop checking for a valid image after 5 seconds
    const imageCheckTimeout = setTimeout(() => {
      onValidImageDetermined(false);
      clearInterval(imageCheck);
    }, 5000);

    return () => {
      clearInterval(imageCheck);
    };
  }, [onValidImageDetermined]);

  const { label, icon, handleClick } = expanded
    ? {
        label: contractAriaLabel,
        icon: <IconContract themed={false} />,
        handleClick: handleContract,
      }
    : {
        label: expandAriaLabel,
        icon: <IconExpand themed={false} />,
        handleClick: handleExpand,
      };

  return (
    <div
      className={styles.container}
      style={{ ...(expanded ? dimensions.expanded : dimensions.contracted) }}
      onTransitionEnd={handleTransitionEnd}
    >
      <img
        className={styles.img}
        ref={img}
        alt={expanded ? expandAriaLabel : contractAriaLabel}
        {...imgAttrs}
      />
      <button
        className={styles.button}
        aria-label={label}
        onClick={handleClick}
      >
        {icon}
      </button>
    </div>
  );
};

export default ExpandableImage;
